import React from 'react';
import { TextVariant } from '@naf/text';
import { CreditCard } from '@styled-icons/material-outlined/CreditCard';
import { Luggage } from '@styled-icons/material-outlined/Luggage';
import { Bolt } from '@styled-icons/material/Bolt';
import { Autorenew } from '@styled-icons/material/Autorenew';
import { ChargingStation } from '@styled-icons/material-outlined';
import { CarModelType, ChargeTest, RangeTest } from '@appTypes/carModelType';
import Nyttelast from './VehicleDataIcons/nyttelast.svg';
import Rekkevidde from './VehicleDataIcons/rekkevidde.svg';
import Tilhengervekt from './VehicleDataIcons/tilhengervekt.svg';
import { getValueString, useVariantData, ValueObject } from './useVariantData';
import * as S from './VehicleData.styles';

type VehicleDataProps = {
  data: CarModelType & { chargeTests?: ChargeTest[]; rangeTests?: RangeTest[] };
  onClick?: (section: string) => void;
};

const DataEntry = ({
  icon,
  label,
  value,
  onClick,
}: {
  icon: React.ReactElement;
  label: string;
  value: string | ValueObject;
  onClick?: () => void;
}) => (
  <S.Entry onClick={onClick}>
    <S.IconContainer>{icon}</S.IconContainer>
    <S.Label variant={TextVariant.ArticleText}>{label}</S.Label>
    <S.Value variant={TextVariant.BodyTextHeader}>{typeof value === 'string' ? value : getValueString(value)}</S.Value>
  </S.Entry>
);

const VehicleData = ({ data, onClick = () => undefined }: VehicleDataProps) => {
  const values = useVariantData(data.vehicleVariants, data.rangeTests, data.chargeTests);
  return (
    <>
      <S.Subheader variant={TextVariant.Header2}>
        {(data.rangeTests?.length || 0) <= 1 ? 'Om testbilen' : 'Om testbilene'}
      </S.Subheader>
      <S.DataList>
        <DataEntry
          icon={<CreditCard size={24} />}
          label="Pris"
          value={{ valueArray: values.prices, unit: 'kr', withPrefix: true }}
        />
        <DataEntry
          onClick={() => onClick('rangeTest')}
          icon={<Rekkevidde />}
          label="Rekkevidde"
          value={{ valueArray: values.ranges, unit: 'km' }}
        />
        <DataEntry
          icon={<Bolt size={24} />}
          label="Forbruk / 100&nbsp;km"
          value={{ valueArray: values.consumption, unit: 'kWh' }}
        />
        <DataEntry
          icon={<Autorenew size={24} />}
          label="Drivhjul"
          value={values.driveTrain.length ? values.driveTrain.join(', ') : 'Ikke oppgitt'}
        />
        <DataEntry
          icon={<ChargingStation size={24} />}
          label="Ladeeffekt"
          value={{ valueArray: values.chargeSpeed, unit: 'kW' }}
        />
        <DataEntry
          onClick={() => onClick('trunkTest')}
          icon={<Luggage size={24} />}
          label="Bagasjerom"
          value={{ valueArray: values.cargospace, unit: 'l' }}
        />
        <DataEntry icon={<Nyttelast />} label="Nyttelast" value={{ valueArray: values.payloads, unit: 'kg' }} />
        <DataEntry
          icon={<Tilhengervekt />}
          label="Tilhengervekt"
          value={values.hasTrailer ? { valueArray: values.trailerPayloads, unit: 'kg' } : 'Ingen hengerfeste'}
        />
      </S.DataList>
    </>
  );
};

export { VehicleData };
